import { useDispatch } from "react-redux";
import { flagStatuses } from "../../../../utils/vars";
import { useCheckPermission } from "../../../../hooks/useCheckPermission";
import { TableSkeleton } from "../../../../components/Skeletons/TableSkeleton";
import { Box, Checkbox, Table } from "@mantine/core";
import {
  addId,
  removeId,
  uncheckHeader,
  useSelectAllSelected,
  useSelectEnabledSingleClientReport,
  useSelectSelected,
  setPageSingleClientReport,
  setSizeSingleClientReport,
  useSelectPageSingleClientReport,
  useSelectReportsSingleClientReport,
  useSelectSizeSingleClientReport,
  useSelectTotalCountSingleClientReport,
} from "../../../../redux/features/reports/singleClientReportSlice";
import { AppPagination } from "../../../../components/AppPagination/AppPagination";
import { TableNoResults } from "../../../../components/TableNoResults/TableNoResults";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useIsFetching } from "@tanstack/react-query";
import { useQueryParams } from "../../../../hooks/useQueryParams";
import HeaderSelectionControl from "./HeaderSectionControl";
import { dateWithHourFormat } from "../../../../utils/dateFormat";

function CellCheckbox({ id }) {
  const allSelected = useSelectAllSelected();
  const selected = useSelectSelected();
  const dispatch = useDispatch();

  const isSelected = allSelected || selected.includes(id);

  function checkboxHandler(checked) {
    if (checked) {
      dispatch(addId(id));
    } else {
      if (allSelected) {
        dispatch(uncheckHeader());
      }
      dispatch(removeId(id));
    }
  }

  return (
    <Checkbox
      checked={isSelected}
      onChange={(ev) => checkboxHandler(ev.currentTarget.checked)}
    />
  );
}

const SingleClientReportTable = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { adjustFilter } = useQueryParams();

  const exportPermission = useCheckPermission("DOWNLOAD_REPORT");

  const data = useSelectReportsSingleClientReport();
  const page = useSelectPageSingleClientReport();
  const size = useSelectSizeSingleClientReport();
  const totalCount = useSelectTotalCountSingleClientReport();

  const enabled = useSelectEnabledSingleClientReport();

  const handlePageChange = (newPage) => {
    adjustFilter("page", newPage - 1);
    dispatch(setPageSingleClientReport(newPage - 1));
  };

  const handleSizeChange = (newSize) => {
    adjustFilter("page", 0);
    adjustFilter("size", newSize);
    dispatch(setSizeSingleClientReport(newSize));
    dispatch(setPageSingleClientReport(0));
  };

  const isFetching = useIsFetching({ queryKey: ["get-single-client-report"] });

  if (!enabled) {
    return null;
  }

  const pageIds = data.map((row) => row.id);

  const rows =
    data.length === 0
      ? [
          <TableNoResults
            key="empty-state"
            colSpan={exportPermission ? 8 : 7}
          />,
        ]
      : data.map((row) => (
          <Table.Tr key={row.id}>
            {exportPermission && (
              <Table.Td style={{ textAlign: "center" }}>
                <CellCheckbox id={row.id} />
              </Table.Td>
            )}
            <Table.Td>{row.typeOfRule}</Table.Td>
            <Table.Td>{row.ruleName}</Table.Td>
            <Table.Td>{flagStatuses[row.caseStatus] || ""}</Table.Td>
            <Table.Td>
              {row.linkToCase ? (
                <a rel="noreferrer" target="_blank" href={row.linkToCase}>
                  {row.linkToCase}
                </a>
              ) : null}
            </Table.Td>
            <Table.Td>{row.userNameAndRole}</Table.Td>
            <Table.Td>
              {row.dateOfCaseStatus
                ? dayjs(row.dateOfCaseStatus).format(dateWithHourFormat)
                : ""}
            </Table.Td>
            <Table.Td>
              {row.dateOfResolutionStatus
                ? dayjs(row.dateOfResolutionStatus).format(dateWithHourFormat)
                : ""}
            </Table.Td>
          </Table.Tr>
        ));
  return (
    <>
      <Box className="table-panel">
        <Table horizontalSpacing="md" highlightOnHover stickyHeader>
          <Table.Thead bg="var(--mantine-color-gray-1)">
            <Table.Tr>
              {exportPermission && (
                <Table.Th style={{ textAlign: "center", width: 120 }}>
                  <HeaderSelectionControl
                    pageIds={pageIds}
                    totalCount={totalCount}
                  />
                </Table.Th>
              )}
              <Table.Th>{t("reports.table.typeOfRule")}</Table.Th>
              <Table.Th>{t("reports.table.ruleName")}</Table.Th>
              <Table.Th>{t("reports.table.caseStatus")}</Table.Th>
              <Table.Th w={600}>{t("reports.table.linkToCase")}</Table.Th>
              <Table.Th>{t("reports.table.usernameAndRole")}</Table.Th>
              <Table.Th>{t("reports.table.dateOfCaseStatus")}</Table.Th>
              <Table.Th>{t("reports.table.dateOfResolutionStatus")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          {isFetching ? (
            <TableSkeleton cols={exportPermission ? 8 : 7} length={size} />
          ) : (
            <Table.Tbody>{rows}</Table.Tbody>
          )}
        </Table>
      </Box>
      {totalCount > 0 && (
        <AppPagination
          page={page}
          take={size}
          total={totalCount}
          onPageChange={handlePageChange}
          onTakeChange={handleSizeChange}
        />
      )}
    </>
  );
};

export default SingleClientReportTable;
